/**
 * Offline Database Service (IndexedDB)
 * Provides structured local storage for offline-first capabilities using the `idb` library.
 *
 * Stores:
 * - reference-data: cached reference tables (countries, divisions, checkpoints, etc.)
 * - reference-metadata: sync timestamps per table
 * - sync-queue: pending operations to replay when back online
 * - cached-searches: cached lot search results for offline lookup
 */

import { type DBSchema, type IDBPDatabase, openDB } from 'idb';

// ─── Schema ────────────────────────────────────────────────────────────────────

const DB_NAME = 'bnft-offline-db';
const DB_VERSION = 2;

export interface ReferenceMetadata {
  lastSync: number; // epoch ms
  count: number;
}

export interface SyncQueueItem {
  id?: number;
  url: string;
  method: string;
  body?: unknown;
  headers?: Record<string, string>;
  timestamp: number;
  retryCount: number;
  maxRetries: number;
  status: 'pending' | 'processing' | 'failed';
  operationType: string;
  metadata?: unknown;
}

export interface CachedSearch {
  key: string;
  data: unknown;
  timestamp: number;
}

interface BnftOfflineDB extends DBSchema {
  'reference-data': {
    key: string;
    value: {
      tableName: string;
      data: unknown[];
    };
  };
  'reference-metadata': {
    key: string;
    value: ReferenceMetadata;
  };
  'sync-queue': {
    key: number;
    value: SyncQueueItem;
    indexes: { 'by-status': string };
  };
  'cached-searches': {
    key: string;
    value: CachedSearch;
    indexes: { 'by-timestamp': number };
  };
  'config': {
    key: string;
    value: { key: string; value: string };
  };
}

// ─── Singleton DB instance ─────────────────────────────────────────────────────

let dbPromise: Promise<IDBPDatabase<BnftOfflineDB>> | null = null;

function getDb(): Promise<IDBPDatabase<BnftOfflineDB>> {
  if (!dbPromise) {
    dbPromise = openDB<BnftOfflineDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Reference data store
        if (!db.objectStoreNames.contains('reference-data')) {
          db.createObjectStore('reference-data', { keyPath: 'tableName' });
        }

        // Reference metadata store
        if (!db.objectStoreNames.contains('reference-metadata')) {
          db.createObjectStore('reference-metadata');
        }

        // Sync queue store (auto-increment id)
        if (!db.objectStoreNames.contains('sync-queue')) {
          const syncStore = db.createObjectStore('sync-queue', {
            keyPath: 'id',
            autoIncrement: true,
          });
          syncStore.createIndex('by-status', 'status');
        }

        // Cached searches store
        if (!db.objectStoreNames.contains('cached-searches')) {
          const searchStore = db.createObjectStore('cached-searches', {
            keyPath: 'key',
          });
          searchStore.createIndex('by-timestamp', 'timestamp');
        }

        // Config store (v2) — persists settings readable by both main thread and Service Worker
        if (!db.objectStoreNames.contains('config')) {
          db.createObjectStore('config', { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

// ─── Reference Data ────────────────────────────────────────────────────────────

export async function putReferenceData(
  tableName: string,
  data: unknown[]
): Promise<void> {
  const db = await getDb();
  await db.put('reference-data', { tableName, data });
}

export async function getReferenceData(
  tableName: string
): Promise<unknown[] | undefined> {
  const db = await getDb();
  const record = await db.get('reference-data', tableName);
  return record?.data;
}

export async function clearReferenceData(): Promise<void> {
  const db = await getDb();
  await db.clear('reference-data');
}

// ─── Reference Metadata ────────────────────────────────────────────────────────

export async function putReferenceMetadata(
  tableName: string,
  meta: ReferenceMetadata
): Promise<void> {
  const db = await getDb();
  await db.put('reference-metadata', meta, tableName);
}

export async function getReferenceMetadata(
  tableName: string
): Promise<ReferenceMetadata | undefined> {
  const db = await getDb();
  return db.get('reference-metadata', tableName);
}

export async function getAllReferenceMetadata(): Promise<
  Record<string, ReferenceMetadata>
> {
  const db = await getDb();
  const keys = await db.getAllKeys('reference-metadata');
  const result: Record<string, ReferenceMetadata> = {};
  for (const key of keys) {
    const val = await db.get('reference-metadata', key);
    if (val) {result[key] = val;}
  }
  return result;
}

// ─── Sync Queue ────────────────────────────────────────────────────────────────

export async function enqueueSyncItem(
  item: Omit<SyncQueueItem, 'id'>
): Promise<number> {
  const db = await getDb();
  return (await db.add('sync-queue', item as SyncQueueItem));
}

export async function getAllSyncItems(): Promise<SyncQueueItem[]> {
  const db = await getDb();
  return db.getAll('sync-queue');
}

export async function getPendingSyncItems(): Promise<SyncQueueItem[]> {
  const db = await getDb();
  return db.getAllFromIndex('sync-queue', 'by-status', 'pending');
}

export async function getPendingSyncCount(): Promise<number> {
  const db = await getDb();
  return db.countFromIndex('sync-queue', 'by-status', 'pending');
}

export async function updateSyncItem(item: SyncQueueItem): Promise<void> {
  const db = await getDb();
  await db.put('sync-queue', item);
}

export async function removeSyncItem(id: number): Promise<void> {
  const db = await getDb();
  await db.delete('sync-queue', id);
}

export async function clearSyncQueue(): Promise<void> {
  const db = await getDb();
  await db.clear('sync-queue');
}

// ─── Cached Searches ───────────────────────────────────────────────────────────

const MAX_CACHED_SEARCHES = 500;

export async function putCachedSearch(
  key: string,
  data: unknown
): Promise<void> {
  const db = await getDb();
  await db.put('cached-searches', { key, data, timestamp: Date.now() });

  // Evict oldest entries if over limit
  const count = await db.count('cached-searches');
  if (count > MAX_CACHED_SEARCHES) {
    const tx = db.transaction('cached-searches', 'readwrite');
    const index = tx.store.index('by-timestamp');
    let cursor = await index.openCursor();
    let toDelete = count - MAX_CACHED_SEARCHES;
    while (cursor && toDelete > 0) {
      await cursor.delete();
      toDelete--;
      cursor = await cursor.continue();
    }
    await tx.done;
  }
}

export async function getCachedSearch(
  key: string
): Promise<unknown | undefined> {
  const db = await getDb();
  const record = await db.get('cached-searches', key);
  return record?.data;
}

export async function clearCachedSearches(): Promise<void> {
  const db = await getDb();
  await db.clear('cached-searches');
}

// ─── Config ─────────────────────────────────────────────────────────────────

/**
 * Store a config value in IndexedDB.
 * Used to share settings (e.g. API base URL) with the Service Worker which
 * cannot access window.__ENV__ or import.meta.env.
 */
export async function putConfig(key: string, value: string): Promise<void> {
  const db = await getDb();
  await db.put('config', { key, value });
}

export async function getConfig(key: string): Promise<string | undefined> {
  const db = await getDb();
  const record = await db.get('config', key);
  return record?.value;
}

export async function deleteConfig(key: string): Promise<void> {
  const db = await getDb();
  await db.delete('config', key);
}
